import type { ReactNode } from 'react';

import { InfoTag } from '@/components/chariot-ui';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import type { Hunt } from '@/types';
import { formatDate } from '@/utils/date.util';

interface OverviewPaneProps {
  hunt: Hunt;
}

export function OverviewPane({ hunt }: OverviewPaneProps) {
  return (
    <div className="grid grid-cols-1 gap-4 p-4 text-sm @md:grid-cols-2">
      <Field label="Iterations" value={hunt.iterationCount} />
      <Field label="Findings" value={hunt.findingsCount} />
      <Field label="Expires" value={formatDate(hunt.expiresAt)} />
      <Field
        label="Last started"
        value={hunt.lastStartedAt ? formatDate(hunt.lastStartedAt) : '—'}
      />
      <Field
        label="Last completed"
        value={hunt.lastCompletedAt ? formatDate(hunt.lastCompletedAt) : '—'}
      />
      <Field label="UUID" value={hunt.uuid} mono />

      {hunt.lastError ? <LastErrorBanner message={hunt.lastError} /> : null}

      <Field label="Prompt" className="col-span-2">
        <MarkdownPreview source={hunt.prompt} />
      </Field>
      <Field label="Finish criteria" className="col-span-2">
        {hunt.finishCriteria ? (
          <MarkdownPreview source={hunt.finishCriteria} />
        ) : (
          <Placeholder>
            Open-ended — hunt loops until expiry or manual pause.
          </Placeholder>
        )}
      </Field>
      <Field label="Guardrails" className="col-span-2">
        {hunt.guardrails ? (
          <MarkdownPreview source={hunt.guardrails} />
        ) : (
          <Placeholder>
            Default safety rules only — no custom judge policy.
          </Placeholder>
        )}
      </Field>
      <Field label="Agent">
        <div className="font-mono text-sm">{hunt.agent || 'hunt'}</div>
      </Field>
      <Field label="Tools">
        {hunt.allowedTools && hunt.allowedTools.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {hunt.allowedTools.map(t => (
              <InfoTag key={t} label={t} icon={null} size="xs" />
            ))}
          </div>
        ) : (
          <Placeholder>All tools the agent declares.</Placeholder>
        )}
      </Field>
    </div>
  );
}

interface FieldProps {
  label: string;
  value?: string | number;
  mono?: boolean;
  className?: string;
  children?: ReactNode;
}

function Field({ label, value, mono, className, children }: FieldProps) {
  return (
    <div className={className}>
      <div className="text-chariot-text-secondary text-xs font-semibold uppercase">
        {label}
      </div>
      <div className={mono ? 'mt-1 font-mono text-xs break-all' : 'mt-1'}>
        {children ?? value}
      </div>
    </div>
  );
}

function Placeholder({ children }: { children: ReactNode }) {
  return (
    <div className="text-chariot-text-secondary text-xs italic">{children}</div>
  );
}

function LastErrorBanner({ message }: { message: string }) {
  return (
    <div className="border-chariot-stroke-critical/50 bg-chariot-stroke-critical/10 col-span-2 rounded-md border p-3">
      <div className="text-chariot-stroke-critical text-xs font-semibold uppercase">
        Last error
      </div>
      <div className="mt-1 text-sm">{message}</div>
    </div>
  );
}
